import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid
} from '@mui/material'
import {
  Add,
  Receipt,
  TrendingUp,
  AttachMoney,
  Warning,
  CheckCircle
} from '@mui/icons-material'

import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import InvoicesTable from '../../components/Invoices/InvoicesTable'
import InvoiceForm from '../../components/Invoices/InvoiceForm'
import { useInvoices } from '../../hooks/useInvoices'
import { useDashboard } from '../../hooks/useDashboard'
import { formatCurrency } from '../../utils/formatters'

const InvoicesPage = () => {
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const [showForm, setShowForm] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  // Get summary data from dashboard
  const { data: dashboardData } = useDashboard('all')
  const { data: summaryData } = useInvoices({ page: 1, limit: 1 })

  const handleCreateInvoice = () => {
    setSelectedInvoice(null)
    setShowForm(true)
  }

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice)
    setShowForm(true)
  }

  const handleViewInvoice = (invoice) => {
    navigate(`/invoices/${invoice.id}`)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedInvoice(null)
  }

  // Calculate summary stats from dashboard data
  const indicators = dashboardData?.indicators || {}
  const totalInvoices = indicators.totals?.invoices || 0
  const paidInvoices = indicators.paid?.invoices || 0
  const pendingInvoices = indicators.pending?.invoices || 0
  const overdueInvoices = indicators.overdue?.invoices || 0

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Facturas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona todas las facturas por cobrar con upload de PDFs
          </Typography>
        </Box>
        
        <RoleGuard requireAdmin fallback={null}>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreateInvoice}
            sx={{ borderRadius: 2 }}
          >
            Nueva Factura
          </Button>
        </RoleGuard>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {totalInvoices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Facturas
                  </Typography>
                </Box>
                <Receipt sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {paidInvoices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pagadas
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {pendingInvoices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por Cobrar
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {overdueInvoices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vencidas
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoices Table */}
      <InvoicesTable 
        onEdit={handleEditInvoice}
        onView={handleViewInvoice}
      />

      {/* Invoice Form Dialog */}
      <InvoiceForm
        open={showForm}
        onClose={handleCloseForm}
        invoice={selectedInvoice}
      />
    </Box>
  )
}

export default InvoicesPage
